/**
 * The analytics range (docs/analytics.md §1): the last 7, 28 (default) or 90 days, ending today in
 * UTC, each compared with the period of the same length just before. The choice lives in the URL
 * (`?range=7`), so a screen can be linked and Back returns to it; 28 is the default and is not
 * written.
 *
 * Pure: dates in, dates out (YYYY-MM-DD strings, UTC). No imports at run time (unit tested under
 * plain Node).
 */

export const RANGES = [7, 28, 90] as const;
export type RangeDays = (typeof RANGES)[number];
export const DEFAULT_RANGE: RangeDays = 28;

/** Inclusive, UTC days: what the read API takes as `from` and `to`. */
export interface DateRange {
  from: string;
  to: string;
}

const DAY = 86_400_000;

const iso = (ms: number) => new Date(ms).toISOString().slice(0, 10);
const ms = (day: string) => Date.parse(`${day}T00:00:00Z`);

/** The range in the URL, or the default for anything else. */
export function parseRange(value: string | number | null | undefined): RangeDays {
  const n = Number(value);
  return (RANGES as readonly number[]).includes(n) ? (n as RangeDays) : DEFAULT_RANGE;
}

/** The last `days` days, ending today (UTC). */
export function rangeFor(days: RangeDays, now: number = Date.now()): DateRange {
  const to = iso(now);
  return { from: iso(ms(to) - (days - 1) * DAY), to };
}

/** The range's last day is today (UTC), so its last point is a day still going. */
export function endsToday(range: DateRange, now: number = Date.now()): boolean {
  return range.to === iso(now);
}

/** The period of the same length just before. */
export function previousRange(range: DateRange): DateRange {
  const length = daysIn(range);
  const to = iso(ms(range.from) - DAY);
  return { from: iso(ms(to) - (length - 1) * DAY), to };
}

/** How many days a range holds (inclusive). */
export function daysIn(range: DateRange): number {
  return Math.round((ms(range.to) - ms(range.from)) / DAY) + 1;
}

/** Every day of a range, in order. */
export function eachDay(range: DateRange): string[] {
  const out: string[] = [];
  for (let t = ms(range.from); t <= ms(range.to); t += DAY) out.push(iso(t));
  return out;
}

/**
 * One value per day of the range from a series that may skip days (a day nobody watched is not
 * sent): missing days are 0, days outside the range are dropped.
 */
export function alignToDays<T extends { day: string }>(series: readonly T[], range: DateRange, value: (row: T) => number): number[] {
  const byDay = new Map(series.map((row) => [row.day, value(row)]));
  return eachDay(range).map((day) => byDay.get(day) ?? 0);
}

export const rangeLabel = (days: RangeDays) => `Last ${days} days`;
